import React, { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import TextField from "../parts/TextField";
import { api } from "../../../api";
import Alert from "../parts/Alert";
import RichTextEditor from "../parts/RichTextEditor/Index";
import Button from "../parts/Button";
import FilesField from "../parts/FilesField";
import CheckboxField from "../parts/Checkbox";
import AutoSuggestShopNameField from "../parts/AutoSuggestShopNameField";
import { useNavigate } from "react-router-dom";
import ButtonSubmit from "../parts/ButtonSubmit";
import FormGroup from "../parts/FormGroup";

type CafeNoteFormProps = {
  cafeNoteId: number;
};

const CafeNoteFormStyle = styled(FormGroup)`
  background: none;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 10px;
`;

const ImageUploadStyle = styled.div`
  display: flex;
  gap: 15px;
  text-align: left;
  flex-wrap: wrap;
  margin-bottom: 10px;
  width: 100%;
`;

const ImageStyle = styled.div`
  position: relative;
  display: inline-block;
  width: calc(100% / 3 - 15px);

  button {
    position: absolute;
    background: #393432;
    border: none;
    border-radius: 25px;
    color: white;
    cursor: pointer;
    font-size: 12px;
    width: 25px;
    height: 25px;
    top: -10px;
    right: -10px;
    padding: 0;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 130px;
  }

  @media (max-width: 767px) {
    width: calc(100% / 2 - 15px);
  }
`;

const ButtonsStyle = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 10px;
`;

const ButtonStyle = styled(ButtonSubmit)`
  max-width: 250px;
  width: 100%;
`;

const DeleteButtonStyle = styled(Button)`
  background: #f2f0e9;
  color: #393432;
  max-width: 250px;
  width: 100%;

  &:hover {
    background: #e0ded7;
  }
`;

const EditCafeNoteForm: React.FC<CafeNoteFormProps> = ({ cafeNoteId }) => {
  const navigate = useNavigate();
  const [shopId, setShopId] = useState<number>();
  const [shopName, setShopName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [visitingDate, setVisitingDate] = useState<string>("");
  const [defaultContentAsHTML, setDefaultContentAsHTML] = useState<string>();
  const [body, setBody] = useState<string>();
  const [images, setImages] = useState<Array<{ id: number; url: string }>>([]);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [message, setMessage] = useState(null);
  const [messageLevel, setMessageLevel] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .getCafeNote(cafeNoteId)
      .then((response) => {
        const shopVisit = response.data;
        setShopId(shopVisit.shop.id);
        if (shopVisit.title) setTitle(shopVisit.title);
        setShopName(shopVisit.shopName);
        setVisitingDate(shopVisit.visitingDate);
        setDefaultContentAsHTML(shopVisit.body);
        setImages(shopVisit.images);
        setIsPublic(shopVisit.isPublic);
      })
      .catch(() => {
        navigate("/user/cafe-notes/create");
      });
  }, []);

  const onClickDeleteImageButton = (index) => {
    const uploadedFiles = [...images];
    uploadedFiles.splice(index, 1);
    setImages(uploadedFiles);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    const params = {
      cafeNote: {
        title,
        visitingDate,
        shopId,
        body,
        images,
        isPublic,
      },
    };
    await api
      .updateCafeNote(cafeNoteId, params)
      .then((response) => {
        setMessage(response.data.message);
        setMessageLevel("success");
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setMessageLevel("error");
      });

    setSubmitting(false);

    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  const onClickDeleteCafeNoteButton = () => {
    const deleteFlag = confirm("削除してよろしいですか？");
    if (!deleteFlag) return;
    api
      .deleteCafeNote(cafeNoteId)
      .then((response) => {
        navigate("/user/cafe-notes", {
          state: {
            message: response.data.message,
            level: "success",
          },
        });
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setMessageLevel("error");
      });
  };

  return (
    <CafeNoteFormStyle onSubmit={handleSubmit}>
      <AutoSuggestShopNameField
        id="shop-name"
        placeholder="店舗名(必須)"
        label="店舗名(必須)"
        mb="20px"
        value={shopName}
        selectShopId={shopId}
        setSelectShopIdAction={setShopId}
        required={true}
      />
      {images.length !== 0 && (
        <ImageUploadStyle>
          {images.map((image, index) => {
            return (
              <ImageStyle key={index}>
                <button onClick={() => onClickDeleteImageButton(index)}>
                  ×
                </button>
                <img src={image.url} alt="店舗画像" />
              </ImageStyle>
            );
          })}
        </ImageUploadStyle>
      )}
      <FilesField
        id="shop-image"
        placeholder="画像※10枚まで"
        mb="20px"
        files={images}
        setFiles={setImages}
        required={false}
        disabled={images.length >= 10}
      />
      <TextField
        type="text"
        label="タイトル"
        placeholder="タイトル"
        mb="20px"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <TextField
        type="date"
        label="訪問日"
        placeholder="訪問日"
        mb="20px"
        required={true}
        value={visitingDate}
        onChange={(event) => setVisitingDate(event.target.value)}
      />
      <RichTextEditor
        setHtml={setBody}
        defaultContentAsHTML={defaultContentAsHTML}
      />
      <CheckboxField
        id="is-public"
        label="ノートを一般公開する"
        mb="20px"
        checked={isPublic}
        onChange={(event) => setIsPublic(event.target.checked)}
      />
      <ButtonsStyle>
        <ButtonStyle
          value="送信"
          submittingMessage="送信中..."
          submitting={submitting}
        />
        <DeleteButtonStyle onClick={onClickDeleteCafeNoteButton}>
          削除する
        </DeleteButtonStyle>
      </ButtonsStyle>
      {message && <Alert level={messageLevel}>{message}</Alert>}
    </CafeNoteFormStyle>
  );
};

export default EditCafeNoteForm;
